/**
 * Countries we support for claim payouts. Drives the bank-country picker on the claims setup page and
 * the country label shown against a beneficiary.
 *
 * `currency` is that country's local currency (lowercase, matching the API's `doc_currency` enum
 * casing), used to default a claim's receipt currency from the member's location — see
 * {@link currencyForCountry}. It is **optional**: omit it where the country has no single payout
 * currency of its own, and the caller will fall through to a better signal (the member's bank account,
 * then their wallet).
 */
export const CLAIMS_SUPPORTED_PAYOUT_COUNTRIES = [
  {
    name: "Australia",
    code: "AU",
    currency: "aud",
  },
  {
    name: "Bangladesh",
    code: "BD",
    currency: "bdt",
  },
  {
    name: "Brazil",
    code: "BR",
    currency: "brl",
  },
  {
    name: "Bulgaria",
    code: "BG",
    // Bulgaria joined the euro on 1 Jan 2026 and ISO 4217 withdrew BGN on 3 Jan, so this followed the
    // server's CountryUtil entry to EUR (Server #645) — Airwallex has no schema for a retired currency.
    currency: "eur",
  },
  {
    name: "Cambodia",
    code: "KH",
    // No currency on purpose. Airwallex cannot settle KHR (or even quote it), so a Cambodian account is
    // paid in whatever currency funds it — USD for XWP, but not universally. Falling through to the
    // member's wallet currency tracks the actual payer; a fixed value here would be wrong for any
    // non-USD supporter. Mirrors the nullable Currency on the server's CountryUtil entry for KH.
  },
  {
    name: "Canada",
    code: "CA",
    currency: "cad",
  },
  {
    name: "China",
    code: "CN",
    currency: "cny",
  },
  {
    name: "Colombia",
    code: "CO",
    currency: "cop",
  },
  {
    name: "Estonia",
    code: "EE",
    currency: "eur",
  },
  {
    name: "France",
    code: "FR",
    currency: "eur",
  },
  {
    name: "Germany",
    code: "DE",
    currency: "eur",
  },
  {
    name: "Hong Kong",
    code: "HK",
    currency: "hkd",
  },
  {
    name: "India",
    code: "IN",
    currency: "inr",
  },
  {
    name: "Ireland",
    code: "IE",
    currency: "eur",
  },
  {
    name: "Israel",
    code: "IL",
    currency: "ils",
  },
  {
    name: "Lithuania",
    code: "LT",
    currency: "eur",
  },
  {
    name: "Malta",
    code: "MT",
    currency: "eur",
  },
  {
    name: "Mexico",
    code: "MX",
    currency: "mxn",
  },
  {
    name: "Netherlands",
    code: "NL",
    currency: "eur",
  },
  {
    name: "New Zealand",
    code: "NZ",
    currency: "nzd",
  },
  {
    name: "Palestine",
    code: "PS",
    // No currency on purpose. Palestine has no currency of its own — ILS, JOD and USD all circulate,
    // and Airwallex settles USD, EUR or ILS into a PS account over SWIFT. So the account is paid in
    // whatever currency funds it, and falling through to the member's wallet tracks the actual payer.
    // Mirrors the nullable Currency on the server's CountryUtil entry for PS.
  },
  {
    name: "Philippines",
    code: "PH",
    currency: "php",
  },
  {
    name: "Poland",
    code: "PL",
    currency: "pln",
  },
  {
    name: "Romania",
    code: "RO",
    currency: "ron",
  },
  {
    name: "Serbia",
    code: "RS",
    currency: "rsd",
  },
  {
    name: "Singapore",
    code: "SG",
    currency: "sgd",
  },
  {
    name: "Spain",
    code: "ES",
    currency: "eur",
  },
  {
    name: "United Kingdom",
    code: "GB",
    currency: "gbp",
  },
  {
    name: "United States",
    code: "US",
    currency: "usd",
  },
  {
    name: "United Arab Emirates",
    code: "AE",
    currency: "aed",
  },
  {
    name: "Uruguay",
    code: "UY",
    currency: "uyu",
  },
];

export function toSupportedCountryCode(countryCode: string | undefined): string | undefined {
  const upper = countryCode?.toUpperCase();
  return CLAIMS_SUPPORTED_PAYOUT_COUNTRIES.find(c => c.code === upper)?.code;
}

export function toSupportedCountryName(countryCode: string | undefined): string | undefined {
  const upper = countryCode?.toUpperCase();
  return CLAIMS_SUPPORTED_PAYOUT_COUNTRIES.find(c => c.code === upper)?.name;
}

/**
 * Local currency (lowercase) for a country code. Undefined if the country is unsupported, or if it has
 * no single local payout currency (e.g. Cambodia) — callers should fall back to a stronger signal in
 * both cases. Case-insensitive on the input so it tolerates the inconsistent casing the server
 * sometimes returns.
 */
export function currencyForCountry(countryCode: string | null | undefined): string | undefined {
  if (!countryCode) return undefined;
  const upper = countryCode.toUpperCase();
  return CLAIMS_SUPPORTED_PAYOUT_COUNTRIES.find(c => c.code === upper)?.currency;
}
